// frontend/src/utils/sequenceCalculations.ts
import type {
  ProductionOrder,
  PriorityCriterio,
  SequenceHeaderKPIs,
  AcabadoType,
  LineConfig,
  CalendarDay,
  RecalcResult,
  OrderComponentRecalc,
} from '@/types/productionSequence';
import { FINISHED_STATUSES } from '@/types/productionSequence';
import type { InventarioRow } from '@/types';
import { peorEstado } from '@/utils/estadoMaterial';

export const ACABADO_RANK: Record<AcabadoType, number> = { SEMI: 0, RG: 1, TST: 2 };

/** Adherencia §6.4 */
export function computeAdherencia(udProducidas: number, udOT: number): number | null {
  if (udProducidas > 0 && udOT > 0) return (udProducidas / udOT) * 100;
  return null;
}

/** Comparador estable de strings/fechas ISO (null al final en ASC). */
function cmpStr(a: string | null, b: string | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a.localeCompare(b, 'es-ES', { numeric: true });
}

/**
 * Referencia del líquido a envasar de una OT = su componente granel ('Q…').
 * - 0 granel → null (estas OTs se ordenan arriba en su grupo de empate).
 * - 1 granel → su itemId.
 * - varios   → el itemId del de mayor consumoEstimado (representativo).
 */
export function liquidoKey(order: ProductionOrder): string | null {
  const graneles = order.componentes.filter(c => c.esGranel);
  if (graneles.length === 0) return null;
  return graneles.reduce((max, c) => (c.consumoEstimado > max.consumoEstimado ? c : max)).itemId;
}

/** Comparador de líquido con null PRIMERO (sin líquido va arriba). */
function cmpLiquidoNullFirst(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  return a.localeCompare(b, 'es-ES', { numeric: true });
}

/**
 * Orden automático §6.1:
 *  P1 TERMINADO/NOTIFICADO → fechaRealFin ASC
 *  P2 PROGRAMADO/LIBERADO → fechaPlanificadaFin ASC, desempate subfamilia → líquido → acabado
 *  P3 ESTIMADO → idem P2
 *  Fallback: fechaPedido ASC; si no, posición original.
 */
export function sortOrders(orders: ProductionOrder[]): ProductionOrder[] {
  const withIndex = orders.map((o, i) => ({ o, i }));

  function bucket(o: ProductionOrder): number {
    if (FINISHED_STATUSES.has(o.estado)) return 0;
    if (o.estado === 'PROGRAMADO' || o.estado === 'LIBERADO' || o.estado === 'INICIADO') return 1;
    if (o.estado === 'ESTIMADO') return 2;
    return 3;
  }

  withIndex.sort((A, B) => {
    const a = A.o, b = B.o;
    const ba = bucket(a), bb = bucket(b);
    if (ba !== bb) return ba - bb;

    if (ba === 0) {
      const c = cmpStr(a.fechaRealFin, b.fechaRealFin);
      if (c !== 0) return c;
    } else {
      // P2/P3: fecha plan fin → subfamilia → líquido (granel Q) → acabado
      const cf = cmpStr(a.fechaPlanificadaFin, b.fechaPlanificadaFin);
      if (cf !== 0) return cf;
      const cs = cmpStr(a.subfamilia, b.subfamilia);
      if (cs !== 0) return cs;
      const cl = cmpLiquidoNullFirst(liquidoKey(a), liquidoKey(b));
      if (cl !== 0) return cl;
      const ca = ACABADO_RANK[a.acabado] - ACABADO_RANK[b.acabado];
      if (ca !== 0) return ca;
    }
    // Fallback común: fechaPedido, luego índice original (estable)
    const cp = cmpStr(a.fechaPedido, b.fechaPedido);
    if (cp !== 0) return cp;
    return A.i - B.i;
  });

  return withIndex.map(x => x.o);
}

/** Sort multi-campo encadenado §6.2 (solo criterios activos, en su orden). */
export function sortByCriterios(
  orders: ProductionOrder[],
  criterios: PriorityCriterio[],
): ProductionOrder[] {
  const active = [...criterios].filter(c => c.active).sort((a, b) => a.order - b.order);
  if (active.length === 0) return [...orders];

  const withIndex = orders.map((o, i) => ({ o, i }));
  withIndex.sort((A, B) => {
    for (const c of active) {
      const dir = c.direction === 'asc' ? 1 : -1;
      const av = A.o[c.field] as unknown;
      const bv = B.o[c.field] as unknown;
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = cmpStr(av == null ? null : String(av), bv == null ? null : String(bv));
      if (cmp !== 0) return dir * cmp;
    }
    return A.i - B.i;
  });
  return withIndex.map(x => x.o);
}

// ── Pools de stock (inventario) ─────────────────────────────────────────────
// Clave de cruce inventario↔componente. Almacén incluido para distinguir traslados.
function poolKey(empresa: string, item: string, color: string | null, estilo: string | null, almacen: string | null): string {
  return [empresa, item, color ?? '', estilo ?? '', almacen ?? ''].join('|');
}
// Clave sin almacén (para detectar stock en OTROS almacenes del mismo artículo).
function articleKey(empresa: string, item: string, color: string | null, estilo: string | null): string {
  return [empresa, item, color ?? '', estilo ?? ''].join('|');
}

/** ¿Está este almacén en España?
 *
 *  El estado ENTRE_ALMACENES significa, literalmente, «hay que mover material
 *  pero está todo en Spain». `sitio` es el `inventsiteid` del maestro y sólo
 *  toma dos valores, `SPAIN` y `ASIA`; ya venía en el contrato de inventario y
 *  nadie lo estaba mirando, así que stock en ASIA o DUBAI contaba como si
 *  estuviera a 40 km. Sin sitio se asume que NO: contar como disponible algo
 *  cuya ubicación no consta es la equivocación cara de las dos. */
function enEspana(r: InventarioRow): boolean {
  return (r.sitio ?? '').trim().toUpperCase() === 'SPAIN';
}

/** Una llegada prevista: material, variante, cuándo y cuánto. */
export interface EntradaPrevista {
  empresa: string;
  itemid: string;
  color?: string | null;
  estilo?: string | null;
  almacen?: string | null;
  /** ISO. Confirmada por el proveedor si la hay; prevista si no. */
  fecha: string;
  qty: number;
}

export interface ComponentPools {
  libre: Map<string, number>;        // poolKey → stock físico disponible
  enPedido: Map<string, number>;     // articleKey → cantidad en pedido (compras en curso)
  porArticulo: Map<string, number>;  // articleKey → stock disponible en almacenes de ESPAÑA
  /**
   * El CALENDARIO de llegadas, ordenado por fecha. Es lo que permite preguntar
   * «¿llega a tiempo?» y no sólo «¿hay bastante?».
   *
   * Va como calendario y no como una fecha ya calculada porque esta pantalla es
   * un SIMULADOR: al arrastrar una orden cambia lo que le falta, y con ello
   * cambia qué entrega se la cubre. Una fecha precalculada sería correcta al
   * medirla y falsa en cuanto alguien reordenara.
   *
   * Ausente si el calendario no se pudo cargar: entonces se decide sólo por
   * cantidad, que es como se decidía antes.
   */
  entradas?: Map<string, EntradaPrevista[]>;
}

export function buildPools(inv: InventarioRow[], entradasPrevistas?: EntradaPrevista[]): ComponentPools {
  const libre = new Map<string, number>();
  const enPedido = new Map<string, number>();
  const porArticulo = new Map<string, number>();
  for (const r of inv) {
    const empresa = r.dataareaid;
    const item = r.articulo;
    const color = r.color ?? null;
    const estilo = r.estilo ?? null;
    const almacen = r.almacen ?? null;
    const pk = poolKey(empresa, item, color, estilo, almacen);
    const ak = articleKey(empresa, item, color, estilo);
    libre.set(pk, (libre.get(pk) ?? 0) + (r.cantidadFisicaDisponible ?? 0));
    // Sólo España: es el pool del que sale ENTRE_ALMACENES, y traer algo de
    // Dubái no es «moverlo entre almacenes».
    if (enEspana(r)) {
      porArticulo.set(ak, (porArticulo.get(ak) ?? 0) + (r.cantidadFisicaDisponible ?? 0));
    }
    enPedido.set(ak, (enPedido.get(ak) ?? 0) + (r.cantidadEnPedido ?? 0) + (r.cantidadOrdenadaReservada ?? 0));
  }
  let entradas: Map<string, EntradaPrevista[]> | undefined;
  if (entradasPrevistas) {
    entradas = new Map();
    for (const e of entradasPrevistas) {
      const ak = articleKey(e.empresa, e.itemid, e.color ?? null, e.estilo ?? null);
      const lista = entradas.get(ak);
      if (lista) lista.push(e); else entradas.set(ak, [e]);
    }
    // Por fecha ascendente: el consumo va cogiendo las más próximas primero, que
    // es el orden en que llegan de verdad.
    for (const lista of entradas.values()) lista.sort((a, b) => a.fecha.localeCompare(b.fecha));
  }

  return { libre, enPedido, porArticulo, entradas };
}

/**
 * ¿Cubren las llegadas anteriores al listón lo que falta?
 *
 * Devuelve `null` si no llegan a tiempo o no hay bastante; si sí, la fecha de la
 * ÚLTIMA entrega necesaria —la que de verdad desbloquea la producción— y una
 * copia de la lista con esas cantidades ya consumidas.
 *
 * Consume porque una entrega cubre una cantidad finita: si dos órdenes
 * dependen del mismo contenedor, la segunda no puede contarlo otra vez. Es el
 * mismo principio que ya seguía el pool de cantidad.
 */
export function cubrenATiempo(
  llegadas: readonly EntradaPrevista[],
  falta: number,
  limite: string | null,
  hoy: string = new Date().toISOString().slice(0, 10),
): { fecha: string; resto: EntradaPrevista[] } | null {
  // Sin fecha de inicio no hay listón contra el que juzgar. Se acepta por
  // cantidad, que es lo que se hacía antes: es preferible a declarar una rotura
  // por un dato que falta.
  const tope = limite ? limite.slice(0, 10) : null;
  let acumulado = 0;
  let fecha: string | null = null;
  const resto: EntradaPrevista[] = [];
  for (const e of llegadas) {
    const f = e.fecha.slice(0, 10);
    // Una entrega VENCIDA y todavía pendiente no avala nada. Su fecha ya pasó,
    // así que sería anterior a cualquier inicio de producción y contaría como
    // puntual para todas las órdenes —una compra de 2021 avalando una
    // producción de 2027—. No sabemos si llegará; lo que sí sabemos es que no
    // llegó cuando tocaba, y afirmar lo contrario es el optimismo que se vino a
    // corregir. Son el 13% del suministro.
    if (f < hoy) {
      resto.push(e);
      continue;
    }
    if (acumulado >= falta || (tope !== null && f > tope)) {
      resto.push(e);
      continue;
    }
    const usa = Math.min(e.qty, falta - acumulado);
    acumulado += usa;
    fecha = e.fecha;
    if (usa < e.qty) resto.push({ ...e, qty: e.qty - usa });
  }
  return acumulado >= falta && fecha ? { fecha, resto } : null;
}


/**
 * Recálculo §5 sobre una secuencia YA ordenada, repartiendo el stock libre por
 * orden. Copia mutable de los pools; cada OT consume su `pendiente` del pool de su
 * componente (la `reserva` está garantizada aparte). Devuelve copia con
 * componentesStatus/granelStatus recalculados y sequencePosition reasignado.
 */
export function recalcComponentStatus(
  orders: ProductionOrder[],
  pools: ComponentPools,
  // COSTURA TEMPORAL — no la quites. Sin ella el recálculo lee el reloj real, y
  // cualquier test con fechas de entrega fijas caduca el día en que esas fechas
  // pasan a ser pasadas (una compra vencida no avala nada): el 2026-10-02 un
  // test que llevaba semanas en verde se puso rojo sin que nadie tocara código.
  // Los tests fijan aquí «hoy»; producción usa el default.
  hoy: string = new Date().toISOString().slice(0, 10),
): ProductionOrder[] {
  const libre = new Map(pools.libre);          // copia mutable: se va consumiendo
  const otros = new Map(pools.porArticulo);     // copia mutable para "otros almacenes"
  const enPedido = new Map(pools.enPedido);     // copia mutable: un pedido cubre cantidad finita
  // Copia mutable del calendario: una entrega que ya cubrió a una orden no
  // puede volver a contarse para la siguiente.
  // Se fija UNA vez para todo el recálculo: si cada componente lo pidiera por su
  // cuenta, una pasada que cruzara la medianoche juzgaría unas filas con un día
  // y otras con el siguiente.
  // Calidad por artículo. `stockCalidadSpain` es del ARTÍCULO y viene repetido
  // en cada fila: se toma UNA vez (el máximo, por si difieren), nunca se suma.
  // Es un pool consumible, como `otros`: lo que cubre a una orden no vuelve a
  // contar para la siguiente.
  const calidad = new Map<string, number>();
  for (const o of orders) {
    for (const c of o.componentes) {
      const q = c.stockCalidadSpain;
      if (q == null || !(q > 0)) continue;
      const ak = articleKey(o.empresa, c.itemId, c.inventcolorid, c.inventstyleid);
      calidad.set(ak, Math.max(calidad.get(ak) ?? 0, q));
    }
  }
  const entradas = pools.entradas
    ? new Map([...pools.entradas].map(([k, v]) => [k, [...v]] as const))
    : undefined;

  return orders.map((o, idx) => {
    let peor: ProductionOrder['componentesStatus'] = 'OK';
    let granel: ProductionOrder['componentesStatus'] = 'OK';
    let granelEncontrado = false;
    const recalc: OrderComponentRecalc[] = [];

    for (const c of o.componentes) {
      const pk = poolKey(o.empresa, c.itemId, c.inventcolorid, c.inventstyleid, c.inventlocationid);
      const ak = articleKey(o.empresa, c.itemId, c.inventcolorid, c.inventstyleid);
      const necesita = c.pendiente;             // lo no reservado

      const disponibleAntes = libre.get(pk) ?? 0;
      // Asignado = lo que realmente cubre el stock físico positivo (nunca negativo).
      const asignado = Math.min(necesita, Math.max(0, disponibleAntes));
      // Se consume la necesidad COMPLETA: el disponible puede quedar negativo y ese
      // déficit se arrastra a la siguiente OT (que arrancará con el acumulado negativo).
      const disponibleDespues = disponibleAntes - necesita;
      libre.set(pk, disponibleDespues);
      otros.set(ak, (otros.get(ak) ?? 0) - asignado);
      const falta = necesita - asignado;

      // El ORDEN de estas preguntas es la especificación, no una casualidad:
      // ENTRE_ALMACENES es «está todo el material en Spain» y COMPRAS_EN_CURSO
      // es «NO está en ningún almacén Spain, pero la compra está hecha». O sea
      // que tener el material manda sobre tener una compra en camino.
      //
      // Estaban al revés: un componente que estaba en RMT y además tenía compra
      // salía naranja, y eso manda a alguien a esperar a un proveedor teniendo
      // el material a 40 km.
      let estado: ProductionOrder['componentesStatus'];
      // Cuándo se desbloquea este componente, si es por una compra. Vacío en el
      // resto de casos: no es una fecha de la misma naturaleza y mezclarlas es
      // lo que hace que alguien planifique contra algo que no ha ocurrido.
      let fechaEstado: string | null = null;
      if (falta <= 0) {
        estado = 'OK';
      } else if ((calidad.get(ak) ?? 0) >= falta) {
        // Basta el stock propio + lo bloqueado en calidad. Se mira en TODA
        // España (`stockCalidadSpain`), no sólo en el almacén que consume: el
        // 60 % está en otro (el depósito aduanero). Va antes que
        // ENTRE_ALMACENES porque es el orden de la cascada del notebook.
        estado = 'PENDIENTE_CALIDAD';
        calidad.set(ak, (calidad.get(ak) ?? 0) - falta);
      } else if ((otros.get(ak) ?? 0) >= falta) {
        estado = 'ENTRE_ALMACENES';
      } else if (entradas) {
        // Con calendario: no basta que haya bastante, tiene que LLEGAR a tiempo.
        // El listón es el inicio planificado, porque el material hace falta
        // cuando se empieza a producir, no cuando se termina — y es el mismo
        // listón que usa el Gantt, así que las dos pantallas juzgan igual.
        const cubre = cubrenATiempo(entradas.get(ak) ?? [], falta, o.fechaInicioPlan, hoy);
        if (cubre) {
          estado = 'COMPRAS_EN_CURSO';
          fechaEstado = cubre.fecha;
          entradas.set(ak, cubre.resto);
        } else {
          // Sin compra, o con una que llega tarde. La spec no los distingue en
          // color: los dos impiden producir en fecha.
          estado = 'ROTURA_STOCK';
        }
      } else if ((enPedido.get(ak) ?? 0) >= falta) {
        // SIN calendario se decide sólo por cantidad, que es como se decidía
        // antes de tener fechas. Es el camino de respaldo si el calendario no
        // cargó: información peor, pero no una rotura inventada.
        estado = 'COMPRAS_EN_CURSO';
        enPedido.set(ak, (enPedido.get(ak) ?? 0) - falta);
      } else {
        estado = 'ROTURA_STOCK';
      }

      recalc.push({
        ...c,
        disponibleAntes,
        asignado,
        disponibleDespues,
        falta,
        estado,
        fechaEstado,
      });

      peor = peorEstado([peor, estado]);
      if (c.esGranel) { granel = estado; granelEncontrado = true; }
    }

    return {
      ...o,
      componentesStatus: peor,
      granelStatus: granelEncontrado ? (granel === 'OK' ? 'OK' : 'PENDIENTE') : o.granelStatus,
      sequencePosition: idx,
      componentesRecalc: recalc,
    };
  });
}

/** KPIs de cabecera §7. */
export function computeKpis(orders: ProductionOrder[]): SequenceHeaderKPIs {
  let totalUdComponentesOK = 0;
  let totalUdGranelOK = 0;
  let ordenEnCursoId: string | null = null;

  for (const o of orders) {
    if (o.componentesStatus === 'OK') totalUdComponentesOK += o.udPendientes;
    if (o.granelStatus === 'OK') totalUdGranelOK += o.udPendientes;
    // "Orden en curso": primera no terminada con producción ya iniciada.
    if (ordenEnCursoId === null && !FINISHED_STATUSES.has(o.estado) && o.udProducidas > 0) {
      ordenEnCursoId = o.id;
    }
  }
  return { totalUdComponentesOK, totalUdGranelOK, ordenEnCursoId };
}

/** Fila destino del auto-scroll inicial de la secuencia (§ auto-scroll en curso). */
export type InitialScrollTarget =
  | { mode: 'none' }
  | { mode: 'bottom' }
  | { mode: 'anchor'; anchorIndex: number; anchorOrderId: string; page: number };

/**
 * Decide dónde posicionar el scroll al entrar: primera fila NO terminada, anclando
 * 2 filas más arriba (contexto). Si todo está terminado → 'bottom'. Lista vacía →
 * 'none'. Con `pageSize` calcula la página 1-indexed del OBJETIVO (para garantizar
 * que la fila en curso queda en pantalla) y recorta el ancla al inicio de esa página
 * (en el borde de página se muestran menos de 2 filas de contexto).
 */
export function computeInitialScrollTarget(
  orders: ProductionOrder[],
  pageSize?: number,
): InitialScrollTarget {
  if (orders.length === 0) return { mode: 'none' };
  const targetIndex = orders.findIndex(o => !FINISHED_STATUSES.has(o.estado));
  if (targetIndex === -1) return { mode: 'bottom' };
  const paginated = pageSize != null && pageSize > 0;
  const page = paginated ? Math.floor(targetIndex / pageSize) + 1 : 1;
  const pageStart = paginated ? (page - 1) * pageSize : 0;
  const anchorIndex = Math.max(targetIndex - 2, pageStart);
  return { mode: 'anchor', anchorIndex, anchorOrderId: orders[anchorIndex].id, page };
}

// ── Recálculo de fechas planificadas (capacidad × calendario) ───────────────

/** Capacidad de la línea en un día laboral completo: UD/turno × turnos. */
export function calcularCapacidadDiaria(config: LineConfig): number {
  return config.udTurnoPromedio * config.turnos;
}

/**
 * Días laborables necesarios para las unidades pendientes. SIEMPRE Math.ceil.
 * 0 si no hay pendiente o la capacidad es inválida.
 */
export function calcularDiasNecesarios(udPendientes: number, capacidadDiaria: number): number {
  if (udPendientes <= 0 || capacidadDiaria <= 0) return 0;
  return Math.ceil(udPendientes / capacidadDiaria);
}

/**
 * Parsea una fecha ISO a UTC a medianoche, tolerando un componente de hora:
 * acepta "YYYY-MM-DD" y "YYYY-MM-DDTHH:MM:SS[...]" (los datos reales de Fabric
 * llegan como datetime). Lanza si la fecha no es válida.
 */
function parseISODate(dateStr: string): Date {
  const d = new Date(`${dateStr.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Fecha ISO inválida: "${dateStr}"`);
  }
  return d;
}

/** ¿Es laborable? Override del calendario; si no, no es sábado(6) ni domingo(0). */
function isWorkday(dateStr: string, calendar: Map<string, boolean>): boolean {
  const key = dateStr.slice(0, 10);
  const override = calendar.get(key);
  if (override !== undefined) return override;
  const day = parseISODate(key).getUTCDay();
  return day !== 0 && day !== 6;
}

/** Suma n días naturales a una fecha ISO en UTC. Devuelve "YYYY-MM-DD". */
function addUTCDays(dateStr: string, n: number): string {
  const d = parseISODate(dateStr);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

const MS_PER_DAY = 86_400_000;

/** Diferencia a − b en días naturales (UTC). */
function diffDiasNaturales(a: string, b: string): number {
  const da = parseISODate(a).getTime();
  const db = parseISODate(b).getTime();
  return Math.round((da - db) / MS_PER_DAY);
}

/**
 * Avanza `diasLaborables` días desde `fechaInicio` (exclusive: el primer día de
 * cómputo es fechaInicio+1), saltando finde y festivos. Tope de seguridad 365.
 */
export function avanzarDiasLaborables(
  fechaInicio: string,
  diasLaborables: number,
  calendar: Map<string, boolean>,
): string {
  if (diasLaborables <= 0) return fechaInicio;
  let cursor = fechaInicio;
  let contados = 0;
  let guard = 0;
  while (contados < diasLaborables) {
    if (++guard > 365) {
      throw new Error(`avanzarDiasLaborables: superadas 365 iteraciones desde ${fechaInicio}`);
    }
    cursor = addUTCDays(cursor, 1);
    if (isWorkday(cursor, calendar)) contados++;
  }
  return cursor;
}

/** CalendarDay[] → Map<fecha, isWorkday> para lookup O(1). */
export function buildCalendarMap(days: CalendarDay[]): Map<string, boolean> {
  return new Map(days.map(d => [d.date, d.isWorkday]));
}

/**
 * Recalcula `fechaPlanificadaFin` para la OT `fromIndex` y posteriores, dentro de
 * UNA línea (el caller debe pasar solo las OTs de la línea, en orden de secuencia).
 * - OTs con índice < fromIndex: intactas.
 * - OTs TERMINADO/NOTIFICADO_TERMINADO: nunca se recalculan; sirven de ancla.
 * - udPendientes <= 0 (no terminada): 0 días, misma fecha que la anterior.
 * - Sin OT anterior con fecha: parte de `hoy`.
 * Inmutable: devuelve arrays/objetos nuevos.
 */
export function recalcularSecuencia(
  orders: ProductionOrder[],
  fromIndex: number,
  config: LineConfig,
  calendar: Map<string, boolean>,
  // NOTE: toISOString() es UTC — los callers deberían pasar `hoy` explícito
  // para evitar off-by-one cuando el reloj local está cerca de medianoche UTC.
  hoy: string = new Date().toISOString().slice(0, 10),
): { updatedOrders: ProductionOrder[]; results: RecalcResult[] } {
  const capacidad = calcularCapacidadDiaria(config);
  const results: RecalcResult[] = [];

  // Fecha-ancla inicial: fin de la OT inmediatamente anterior (con fecha), o hoy.
  // Se normaliza a "YYYY-MM-DD" porque los datos reales pueden traer hora.
  let prevFin: string | null = null;
  for (let i = fromIndex - 1; i >= 0; i--) {
    const f = orders[i].fechaPlanificadaFin;
    if (f) { prevFin = f.slice(0, 10); break; }
  }

  const updatedOrders = orders.map((o, idx) => {
    if (idx < fromIndex) return o;
    if (FINISHED_STATUSES.has(o.estado)) {
      if (o.fechaPlanificadaFin) prevFin = o.fechaPlanificadaFin.slice(0, 10);
      return o;
    }

    const inicioBase = prevFin ?? hoy;
    let dias: number;
    let newFin: string;
    if (o.udPendientes <= 0) {
      dias = 0;
      newFin = inicioBase;
    } else {
      dias = calcularDiasNecesarios(o.udPendientes, capacidad);
      newFin = avanzarDiasLaborables(inicioBase, dias, calendar);
    }

    const old = o.fechaPlanificadaFin;
    const delta = old ? diffDiasNaturales(old, newFin) : 0;

    results.push({
      orderId: o.id,
      oldFechaPlanificadaFin: old,
      newFechaPlanificadaFin: newFin,
      diasNecesarios: dias,
      fechaInicio: inicioBase,
    });

    prevFin = newFin;
    return { ...o, fechaPlanificadaFin: newFin, isRecalculated: true, recalcDelta: delta };
  });

  return { updatedOrders, results };
}

/** Resumen de cabecera del drawer de componentes. */
export function summarizeComponentesRecalc(comps: OrderComponentRecalc[]): {
  count: number; rotura: number; faltaTotal: number;
} {
  return {
    count: comps.length,
    rotura: comps.filter(c => c.estado === 'ROTURA_STOCK').length,
    faltaTotal: comps.reduce((s, c) => s + Math.max(0, c.falta), 0),
  };
}
